import type {
  AnalyticElementCondition,
  AnalyticElementMatch,
  AnalyticEventConfig,
  AnalyticPageMatch,
} from 'types/analytic-tracking'

export function matchesAnalyticPage(match: AnalyticPageMatch, pathname: string): boolean {
  const normalize = (value: string) => value.replace(/\/+$/, '') || '/'
  const current = normalize(pathname)
  const expected = normalize(match.value.startsWith('/') ? match.value : `/${match.value}`)

  switch (match.type) {
    case 'pathname':
      return current === expected
    case 'prefix':
      return current === expected || current.startsWith(`${expected}/`)
    case 'regex':
      return new RegExp(match.value).test(pathname)
    default:
      return false
  }
}

function satisfiesConditions(element: Element, conditions?: AnalyticElementCondition[]): boolean {
  if (!conditions?.length) return true

  return conditions.every((condition) => {
    if (condition.type === 'text') {
      return element.textContent?.trim() === condition.value
    }
    return element.getAttribute(condition.name) === condition.value
  })
}

function elementMatchesSelector(clickTarget: Element, selector: string): Element | null {
  if (clickTarget.matches(selector)) return clickTarget
  return clickTarget.closest(selector)
}

const TRACK_ATTR_SELECTORS = '[data-analytic-track]'

/** Umami's own auto-track attributes — usable with no `events` entry in ANALYTICS_TRACKING_CONFIG. */
const UMAMI_EVENT_ATTR = 'data-umami-event'
const UMAMI_EVENT_DATA_PREFIX = 'data-umami-event-'

function getTrackId(element: Element | null): string | null {
  if (!element) return null
  return element.getAttribute('data-analytic-track')
}

export function resolveUmamiEventElement(clickTarget: EventTarget | null): HTMLElement | null {
  if (!(clickTarget instanceof Element)) return null
  return clickTarget.closest(`[${UMAMI_EVENT_ATTR}]`) as HTMLElement | null
}

/**
 * Read `data-umami-event` plus any `data-umami-event-*` extras, matching the tracker script's
 * own convention (`data-umami-event-source="nav_bar"` → `{ source: 'nav_bar' }`).
 */
export function readUmamiEventAttributes(
  element: HTMLElement | null,
): { event: string; data: Record<string, string> } | null {
  const event = element?.getAttribute(UMAMI_EVENT_ATTR)?.trim()
  if (!element || !event) return null

  const data: Record<string, string> = {}

  Array.from(element.attributes).forEach(({ name, value }) => {
    if (!name.startsWith(UMAMI_EVENT_DATA_PREFIX)) return
    const key = name.slice(UMAMI_EVENT_DATA_PREFIX.length)
    if (key) data[key] = value
  })

  return { event, data }
}

export function resolveAnalyticTrackElement(clickTarget: EventTarget | null): HTMLElement | null {
  if (!(clickTarget instanceof Element)) return null
  return clickTarget.closest(TRACK_ATTR_SELECTORS) as HTMLElement | null
}

export function elementMatchesAnalyticConfig(
  clickTarget: Element,
  trackElement: HTMLElement | null,
  elementMatch: AnalyticElementMatch,
  eventId: string,
): boolean {
  if (getTrackId(trackElement) === eventId) {
    return satisfiesConditions(trackElement as Element, elementMatch.conditions)
  }

  if (!elementMatch.selector) return false

  const matched = elementMatchesSelector(clickTarget, elementMatch.selector)
  if (!matched) return false

  return satisfiesConditions(matched, elementMatch.conditions)
}

export function resolveAnalyticEventConfig(
  clickTarget: EventTarget | null,
  pathname: string,
  events: AnalyticEventConfig[] | undefined,
): { config: AnalyticEventConfig; trackElement: HTMLElement | null } | null {
  if (!events?.length || !(clickTarget instanceof Element)) return null

  const trackElement = resolveAnalyticTrackElement(clickTarget)

  for (const config of events) {
    if (config.enabled === false) continue
    if (!config.match.element) continue // page-view style event, not click-triggered
    if (!matchesAnalyticPage(config.match.page, pathname)) continue
    if (!elementMatchesAnalyticConfig(clickTarget, trackElement, config.match.element, config.id)) continue

    return { config, trackElement }
  }

  return null
}
